import React from 'react'
import { Helmet } from 'react-helmet-async'
import type { Metadata } from 'next'
import ServicePageTemplate, { ServicePageConfig } from '@/components/templates/ServicePageTemplate'

export const metadata: Metadata = {
  title: 'Private Dining | Member Services | FGA Prestige',
  description: 'Exclusive culinary experiences with world-renowned chefs. Reservations at hidden gems and Michelin-starred restaurants with VIP treatment.',
  openGraph: {
    title: 'Private Dining | Member Services | FGA Prestige',
    description: 'Exclusive culinary experiences with world-renowned chefs. Reservations at hidden gems and Michelin-starred restaurants with VIP treatment.',
    url: 'https://fgaprestige.com/member-services/private-dining',
    images: [
      {
        url: '/Member Services/Private Dining/hero.png',
        width: 1200,
        height: 630,
        alt: 'FGA Prestige Private Dining',
      },
    ],
  },
}

const privateDiningConfig: ServicePageConfig = {
  title: 'Private Dining - FGA Prestige',
  description: 'Exclusive culinary experiences with world-renowned chefs.',
  slug: 'private-dining',
  heroImage: '/Member Services/Private Dining/hero.png',
  serviceName: 'PRIVATE DINING',
  whySectionA: {
    title: 'Elevated Encounters Private Dining by FGA Prestige',
    content: 'Step beyond the ordinary and into a realm where every meal is a celebration, every table a story. Our private dining experiences in Hong Kong unlock a world of gastronomic discovery—from intimate chef’s tables tucked away in storied establishments to opulent venues with sweeping skyline views. Sink into supple leather chairs, bask in the soft glow of candlelight, and savour menus curated by the city’s culinary maestros—each course tailored to your preferences, every pairing thoughtfully considered.',
    image: '/Member Services/Private Dining/body/1.png',
    imageAlt: 'Michelin-starred Private Dining Experience'
  },
  whySectionC: {
    title: 'Your Table, Your Moment. At Your Service',
    content: 'With seamless service and absolute discretion, your every wish is anticipated: rare vintages decanted, favourite dishes reborn, and dietary preferences fulfilled without a word. Whether marking milestones or hosting quiet gatherings, your time at the table is set to a backdrop of refined hospitality and effortless luxury.',
    image: '/Member Services/Private Dining/body/2.png',
    imageAlt: 'Michelin-starred Private Dining Experience'
  },
  whySectionB: {
    title: 'The City Is Your Menu',
    benefits: [
      {
        title: '',
        description: 'Hong Kong’s vibrant flavours are at your fingertips. Let us reserve the most coveted seats, orchestrate exclusive tastings, or bring the city’s star chefs to your private setting for an unforgettable feast. While others chase reservations, you simply arrive—your table awaits, and the city’s finest cuisine is yours to enjoy, crafted entirely around you.'
      }
    ],
    image: '/Member Services/Private Dining/body/3.png',
    imageAlt: 'Luxury Private Dining Services'
  },
  masterpieceBackgroundImage: '/Member Services/Private Dining/CTA Banner/banner.png'
}

export default function PrivateDiningPage() {
  return (
    <>
      <Helmet>
        <title>Private Dining | Member Services | FGA Prestige</title>
        <meta name="description" content="Exclusive culinary experiences with world-renowned chefs. Reservations at hidden gems and Michelin-starred restaurants with VIP treatment." />
        <link rel="canonical" href="https://fgaprestige.com/member-services/private-dining" />
        <meta property="og:title" content="Private Dining | Member Services | FGA Prestige" />
        <meta property="og:description" content="Exclusive culinary experiences with world-renowned chefs. Reservations at hidden gems and Michelin-starred restaurants with VIP treatment." />
        <meta property="og:url" content="https://fgaprestige.com/member-services/private-dining" />
        <meta property="og:image" content="/Member Services/Private Dining/hero.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FGA Prestige Private Dining" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Private Dining | Member Services | FGA Prestige" />
        <meta name="twitter:description" content="Exclusive culinary experiences with world-renowned chefs. Reservations at hidden gems and Michelin-starred restaurants with VIP treatment." />
        <meta name="twitter:image" content="/Member Services/Private Dining/hero.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Private Dining",
            "description": "Exclusive culinary experiences with world-renowned chefs. Reservations at hidden gems and Michelin-starred restaurants with VIP treatment.",
            "provider": {
              "@type": "Organization",
              "name": "FGA Prestige",
              "url": "https://fgaprestige.com"
            },
            "url": "https://fgaprestige.com/member-services/private-dining",
            "image": "/Member Services/Private Dining/hero.png"
          })}
        </script>
      </Helmet>
      <ServicePageTemplate config={privateDiningConfig} />
    </>
  )
}